<template>
    <ion-page class="items-center">
        <ion-content class="max-w-lg">
            <div class="login mx-6">
                <h4 class="my-0! uppercase">
                    <strong>{{ $t("project.name") }}</strong>
                </h4>
                <h5 class="mt-0! uppercase">
                    {{ $t("project.app_name") }}
                </h5>
                <h1 class="login-title">
                    {{ $t("login.titel") }}
                </h1>
                <form
                    novalidate
                    class="flex flex-col mx-6"
                    v-if="!userStore.user"
                    @submit.prevent="onSubmit"
                >
                    <BaseInput
                        data-cy="email"
                        :readonly="!lifecycleStore.isAppOnline"
                        v-model="loginData.emailAddress"
                        :label="t('login.email')"
                        input-type="email"
                    />

                    <BaseInput
                        data-cy="passwort"
                        :readonly="!lifecycleStore.isAppOnline"
                        class="mt-6"
                        v-model="loginData.password"
                        :label="t('login.pw')"
                        input-type="password"
                    />

                    <ion-button
                        type="submit"
                        class="text-lg ml-0 mt-10"
                        shape="round"
                        data-cy="button-login"
                        :disabled="isLoginRequestRunning"
                    >
                        <span v-if="!isLoginRequestRunning">{{ t("login.button") }}</span>
                        <ion-spinner
                            v-if="isLoginRequestRunning"
                            color="light"
                            size="small"
                        />
                    </ion-button>
                </form>

                <p class="mt-4 text-sm text-red-400">
                    {{ alertMessage.header }}
                </p>
                <p class="mt-1 text-sm">
                    {{ alertMessage.body }}
                </p>

                <div v-if="userStore.user">
                    <p>Eingeloggter Benutzer: {{ userStore?.user.username }}</p>
                    <ion-button
                        class="mt-4"
                        @click="userStore.logoutUser"
                    >
                        {{ $t('logout.button') }}
                    </ion-button>
                </div>

                <div class="mt-4 text-xs">
                    <div>{{ $t('login.info') }}</div>
                    <div class="mb-4">{{ $t('login.getInTouch') }}</div>
                    <a :href="'mailto:app@virtualshepherd.at?subject=Registrierung'">
                        app@virtualshepherd.at
                    </a>
                    <div class="mt-4">{{ $t('login.copyright') }}</div>
                </div>
                <div
                    v-if="!lifecycleStore.isAppOnline"
                    class="w-full flex gap-4 mt-10 flex justify-center text-2xl"
                >
                    <IonIcon
                        size="large"
                        class="warning-icon"
                        :icon="alertCircleOutline"
                    />
                    <span>{{ $t('login.noInternet') }}</span>
                </div>
            </div>
        </ion-content>
    </ion-page>
</template>

<script
    setup
    lang="ts"
>

import { authenticate } from "@/api/auth";
import { useAppLifecycleStore } from "@/store/app-lifecycle";
import { useUserStore } from "@/store/user";
import { IonButton, IonContent, IonIcon, IonPage, IonSpinner, onIonViewWillEnter } from "@ionic/vue";
import { alertCircleOutline } from "ionicons/icons";
import { BaseInput } from "@movinglayers/controls";
import { useLogin } from "@movinglayers/signing";
import { useI18n } from 'vue-i18n';

const { t } = useI18n({ useScope: 'global' });

const lifecycleStore = useAppLifecycleStore();
const userStore = useUserStore();

// @ts-ignore some unexpected ts error with AxiosRequestConfig, TODO: follow this issue: https://github.com/axios/axios/issues/6692
const { login, loginData, alertMessage, resetLoginData, isLoginRequestRunning } = useLogin(authenticate);

const onSubmit = async () => {
    try {
        const response = await login();
        const { jwt, user } = response?.data;

        if (user && jwt) {
            await userStore.saveJwt(jwt);
            userStore.user = user;
            loginData.emailAddress = '';
            loginData.password = '';
            lifecycleStore.afterAuthenticating(true);
        } else {
            alertMessage.header = t('login.failedTitle');
            alertMessage.body = t('login.failedBody');
        }
    } catch (error) {
        if (error.status >= 400 && error.status < 500) {
            alertMessage.header = t('login.failedTitle');
            alertMessage.body = t('login.failedBody');
        } else {
            alertMessage.header = t('login.noConnectionToServerTitle');
            alertMessage.body = t('login.noConnectionToServerBody');
        }
    }
};

onIonViewWillEnter(() => {
    resetLoginData();
});

</script>


<style scoped>
.login {
    min-height: 90vh;
    margin-top: -50px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: var(--white100);

    @media (max-height: 650px) {
        margin-top: 0;
        min-height: 75vh;
    }
}

.login-title {
    font-size: 64px;
    text-transform: uppercase;
}

ion-button {
    --background-hover: var(--action-button-color, #59c4f6);
    --background: var(--action-button-color, #59c4f6);
    --color: var(--ion-color-light-contrast);
}

ion-icon.warning-icon {
    color: red;
}
</style>